import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Form, Input, InputNumber, Radio, Select, Space, Tag } from 'antd';
import {
  AudioOutlined,
  DesktopOutlined,
  PhoneOutlined,
  SoundOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import NProgress from 'nprogress';
import useMessageRedux from '@/store/message-redux';
import {
  getAiHotlineSettings,
  saveAiHotlineSettings,
  type AiHotlineSettings,
} from '@/facilities/modules/aiHotlineSettings';

const PHONE_RE = /^\+[1-9]\d{7,14}$/;

const DEFAULTS: AiHotlineSettings = {
  transferPhoneNumber: '',
  transferTimeoutSeconds: 25,
  transferWhisper: '',
  contractors: [],
  takeoverTarget: 'phone',
  agentVoice: 'male',
};

/**
 * TMS settings. First section: the AI hotline — where the AI agent hands a call over when the
 * caller asks for a human officer. The bot reads these values at the moment of the transfer, so a
 * change here takes effect on the next call without any redeploy.
 */
export default function Settings() {
  const { t } = useTranslation();
  const { message } = useMessageRedux();
  const [form] = Form.useForm<AiHotlineSettings>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<AiHotlineSettings>(DEFAULTS);
  const number = Form.useWatch('transferPhoneNumber', form);

  useEffect(() => {
    NProgress.done();
    (async () => {
      try {
        const res = await getAiHotlineSettings();
        const data: AiHotlineSettings = { ...DEFAULTS, ...(res.data?.body || {}) };
        setSaved(data);
        form.setFieldsValue(data);
      } catch (error: unknown) {
        message.error(String((error as { detail?: string })?.detail || error));
      } finally {
        setLoading(false);
      }
    })();
  }, [form, message]);

  const onSave = async (values: AiHotlineSettings) => {
    setSaving(true);
    try {
      const res = await saveAiHotlineSettings({
        ...values,
        transferPhoneNumber: (values.transferPhoneNumber || '').trim(),
        transferWhisper: (values.transferWhisper || '').trim(),
      });
      const data: AiHotlineSettings = { ...DEFAULTS, ...(res.data?.body || values) };
      setSaved(data);
      form.setFieldsValue(data);
      message.success(t('success'));
    } catch (error: unknown) {
      message.error(String((error as { detail?: string })?.detail || error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-[820px]">
      <h1 className="text-[22px] font-semibold mb-4">{t('settings')}</h1>
      <Card
        loading={loading}
        title={
          <span className="flex items-center gap-2">
            <PhoneOutlined /> {t('settings_aiHotline')}
            {saved.transferPhoneNumber ? (
              <Tag color="success" bordered={false} className="rounded-full ml-1">
                {t('settings_transferOn')} · {saved.transferPhoneNumber}
              </Tag>
            ) : (
              <Tag color="warning" bordered={false} className="rounded-full ml-1">
                {t('settings_transferOff')}
              </Tag>
            )}
          </span>
        }
      >
        <p className="text-[13px] text-[#00000099] dark:text-[#ffffff99] mb-5 -mt-1">
          {t('settings_aiHotlineHint')}
        </p>
        <Form<AiHotlineSettings>
          form={form}
          layout="vertical"
          initialValues={DEFAULTS}
          onFinish={onSave}
          requiredMark={false}
        >
          <Form.Item
            name="agentVoice"
            label={
              <span>
                <AudioOutlined /> {t('settings_agentVoice')}
              </span>
            }
            extra={t('settings_agentVoiceHint')}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: t('settings_voiceMale'), value: 'male' },
                { label: t('settings_voiceFemale'), value: 'female' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="transferPhoneNumber"
            label={t('settings_transferNumber')}
            extra={t('settings_transferNumberHint')}
            rules={[
              {
                validator: (_, v: string) =>
                  !v || PHONE_RE.test(v.trim())
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('settings_transferNumberInvalid'))),
              },
            ]}
          >
            <Input
              placeholder="+6591234567"
              prefix={<PhoneOutlined className="text-[#00000073]" />}
              allowClear
              className="max-w-[320px]"
              data-testid="settings-transfer-number"
            />
          </Form.Item>
          <Form.Item
            name="transferTimeoutSeconds"
            label={t('settings_transferTimeout')}
            extra={t('settings_transferTimeoutHint')}
            rules={[{ required: true, type: 'number', min: 10, max: 60 }]}
          >
            <InputNumber min={10} max={60} addonAfter="s" className="w-[160px]" />
          </Form.Item>
          <Form.Item
            name="takeoverTarget"
            label={
              <span>
                <DesktopOutlined /> {t('settings_takeoverTarget')}
              </span>
            }
            extra={t('settings_takeoverHint')}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: t('settings_takeoverPhone'), value: 'phone' },
                { label: t('settings_takeoverBrowser'), value: 'browser' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="transferWhisper"
            label={
              <span className="flex items-center gap-2">
                <SoundOutlined /> {t('settings_transferWhisper')}
              </span>
            }
            extra={t('settings_transferWhisperHint')}
            rules={[{ max: 200 }]}
          >
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            name="contractors"
            label={
              <span className="flex items-center gap-2">
                <TeamOutlined /> {t('wf_contractorSetting')}
              </span>
            }
            extra={t('wf_contractorSettingHint')}
          >
            <Select
              mode="tags"
              tokenSeparators={[',']}
              open={false}
              className="max-w-[560px]"
            />
          </Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              data-testid="settings-save"
            >
              {t('save')}
            </Button>
            <Button onClick={() => form.setFieldsValue(saved)} disabled={saving}>
              {t('reset')}
            </Button>
            {!number && (
              <span className="text-[12px] text-[#faad14]">
                {t('settings_transferOffHint')}
              </span>
            )}
          </Space>
        </Form>
      </Card>
    </div>
  );
}
